import './index.css'

import { MdInfoOutline } from '@react-icons/all-files/md/MdInfoOutline'
import cn from 'clsx'
import * as React from 'react'
import type { Location } from 'react-router-dom'
import { useParams } from 'react-router-dom'
import validation from 'react-validation-mixin'
import strategy from 'react-validatorjs-strategy'
import InlineSVG from 'svg-inline-react'

import mixpanel from '@disco/api/mixpanel'
import { DISCO_SITE_SIGNUP_FROM_PUBLIC_INBOX_URL } from '@disco/constants/external-links'
import dayjs from '@disco/utils/dayjs'
import { getUserDisplayName, sanitize } from '@disco/utils/string-utils'
import { formatDateWithTimezone } from '@disco/utils/time-utils'

import type { BusinessData } from 'types/business'
import type { DiscoPlaylist, InboxGroup } from 'types/inbox'
import type { Business } from 'types/public-playlist'
import type { User } from 'types/user'

import { Box, Callout, Flex, Text, TextArea, TextField } from 'components/ds'
import PublicContent from 'components/public/content'
import PublicHeader from 'components/public/header'
import Accordion from 'components/ui/accordion'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import Label from 'components/ui/label'
import MatchMedia from 'components/ui/match-media'
import VerticalSpacer from 'components/ui/vertical-spacer'

import DiscoPlaylistActions from '../disco-playlist-actions'
import DiscoPlaylistLink from '../disco-playlist-link'
import FooterLogo from '../footer-logo'
import Playlist from '../playlist'
import { FileReceiverSuccessMessage } from './file-receiver-success-message'

type State = {
  sender: string
  email: string
  title: string
  message: string
}

type OwnProps = {
  businessData: BusinessData
  onFileSelect: (...args: Array<any>) => any
  playlist: {
    tracks: Record<string, any>[] | null | undefined
    tempItems: Record<string, any>[] | null | undefined
    sending: boolean
    sent: boolean
    error: Record<string, any> | null | undefined
  }
  inbox: InboxGroup
  discoPlaylist: DiscoPlaylist
  discoUser: User | null | undefined
  businessList: Business[]
  onUploadCancel: (...args: Array<any>) => any
  deleteTrack: (...args: Array<any>) => any
  sendPlaylist: (...args: Array<any>) => any
  sendDiscoPlaylist: (...args: Array<any>) => any
  setDiscoPlaylist: (...args: Array<any>) => any
  customMessage?: string | null | undefined
  params: {
    path: string
    groupId: string
    groupName: string
  }
  location: Location
  app: Record<string, any>
  formCaption: string
  submitButtonCaption: {
    caption: string
    progress: string
  }
  preview?: boolean
}

type ValidationProps = OwnProps & {
  getValidationMessages: (...args: Array<any>) => any
  validate: (...args: Array<any>) => any
}

class FileReceiver extends React.Component<ValidationProps, State> {
  dropzone: Record<string, any> | null | undefined
  state = {
    sender: this.props.inbox.brief_recipient?.name ?? '',
    email: this.props.inbox.brief_recipient?.email ?? '',
    title:
      this.props.inbox.brief_recipient && this.props.inbox.brief_data
        ? `${
            Boolean(this.props.inbox.brief_recipient?.name)
              ? this.props.inbox.brief_recipient?.name
              : this.props.inbox.brief_recipient?.email
          } - ${this.props.inbox.brief_data?.name}`
        : '',
    message: '',
  }

  _onChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    if (this.props.preview) return
    this.setState(prevState => ({
      ...prevState,
      [e.target.name as keyof State]: e.target.value,
    }))
  }

  validatorTypes = strategy.createSchema({
    name: 'required',
    email: 'required|email',
    playlist: 'required',
    tracks: 'required|array|min:1',
  })

  renderHelpText = message => {
    return <div className="warning-block">{message}</div>
  }

  render() {
    const avatar = this.props.inbox.image ?? this.props.businessData.image
    const { sent } = this.props.playlist

    const senderName =
      this.props.inbox.brief_data?.sender_name ?? this.props.inbox.name

    if (sent) {
      return (
        <FileReceiverSuccessMessage
          isBrief={!!this.props.inbox.brief_data}
          playlistName={this.getPlaylistName()}
        />
      )
    }

    return (
      <div className="public-container FileReceiver">
        <>
          {this.props.preview && (
            <Box p="4">
              <Alert status="warning" icon="warning">
                Preview only. Do not share this link.
              </Alert>
            </Box>
          )}
          {!sent && this.props.inbox.brief_data && !this.props.preview ? (
            <Box p="4">
              <Alert status="warning" icon="warning">
                This link is unique to{' '}
                <b>
                  {Boolean(this.props.inbox.brief_recipient?.name)
                    ? this.props.inbox.brief_recipient.name
                    : 'you'}
                </b>
                . Please do not share this outside of your organization.{' '}
                <b>{senderName}</b> will be notified that you have viewed this
                brief.
              </Alert>
            </Box>
          ) : null}
        </>
        <PublicHeader avatar={avatar}>
          <div className="public-header__right-elements">
            <MatchMedia media="(max-width: 600px)">
              <h1 className="FileReceiver_Name--phone">
                {this.props.formCaption}
              </h1>
            </MatchMedia>

            {!sent && (
              <DiscoPlaylistLink
                searchParams={this.props.location.search}
                disabled={this.props.preview}
              />
            )}
          </div>
        </PublicHeader>
        {this._renderBody()}
      </div>
    )
  }

  getSender = () => {
    const { discoUser } = this.props
    return discoUser?.id ? getUserDisplayName(discoUser) : this.state.sender
  }
  getPlaylistName = () => {
    const { discoPlaylist } = this.props
    return discoPlaylist ? discoPlaylist.name : this.state.title
  }
  getSenderEmail = () => {
    const { discoUser } = this.props
    return discoUser?.id ? discoUser.email : this.state.email
  }
  getValidatorData = () => {
    return {
      name: this.getSender().trim(),
      email: this.getSenderEmail().trim(),
      playlist: this.getPlaylistName().trim(),
      // if playlist is from DISCO assume that it contains non zero number of
      // tracks
      tracks: this.props.discoPlaylist ? [{}] : this.props.playlist.tempItems,
    }
  }

  _renderBody() {
    const { sent, sending, error } = this.props.playlist
    const { customMessage, discoPlaylist } = this.props

    const lastUpdatedDate = this.props.inbox.brief_data?.content_modified
      ? formatDateWithTimezone(
          this.props.inbox.brief_data?.content_modified,
          this.props.inbox.brief_data?.deadline_timezone,
        )
      : undefined

    const nameMessage = this.props.getValidationMessages('name').length
      ? 'Name is required'
      : null
    const emailMessage = this.props.getValidationMessages('email').length
      ? 'Invalid email address'
      : null
    const playlistNameMessage = this.props.getValidationMessages('playlist')
      .length
      ? 'Playlist Name is required'
      : null
    let playlistMessage = this.props.getValidationMessages('tracks').length
      ? 'Playlist must contain at least 1 item'
      : null

    if (error) {
      let serverMessage = error.toString()

      if (error.body && error.body[0] && typeof error.body[0] === 'string') {
        serverMessage = `Error: ${error.body[0]}` // example of serverMessage:
        //
        //     Error: form has already been submitted
      }

      playlistMessage = serverMessage
    }

    const isSendDisabled = this._isSendDisabled()

    const senderDefaultValue = this.getSender()
    const playlistDefaultName = this.getPlaylistName()
    const emailDefaultValue = this.getSenderEmail()
    const isMobile = this.props.app.windowWidth <= 600
    return (
      <PublicContent plane>
        <form>
          <div className="FileReciever_Form">
            <MatchMedia media="(min-width: 601px)">
              <>
                <h1
                  className={cn('FileReceiver_Name hidden-phone', {
                    'FileReceiver_Name--brief':
                      this.props.inbox.brief_data?.deadline,
                  })}
                >
                  {this.props.formCaption}
                </h1>
                {this.props.inbox.brief_data?.deadline ? (
                  <Box
                    display="inline-block"
                    className="FileReceiver_Name--brief"
                  >
                    <Callout.Root color="gray">
                      <Callout.Icon>
                        <MdInfoOutline />
                      </Callout.Icon>
                      <Callout.Text>
                        Deadline:{' '}
                        {formatDateWithTimezone(
                          this.props.inbox.brief_data?.deadline,
                          this.props.inbox.brief_data?.deadline_timezone,
                        )}{' '}
                        {/* @ts-ignore */}(
                        {dayjs(this.props.inbox.brief_data?.deadline).fromNow()}
                        )
                      </Callout.Text>
                    </Callout.Root>
                  </Box>
                ) : null}
              </>
            </MatchMedia>
            {this.props.inbox.brief_data?.description_html ? (
              <Box maxWidth="680px" my="4">
                <Accordion
                  title={
                    <Text weight="bold" size="4" mr="1">
                      Brief Details
                    </Text>
                  }
                  titleClassName="FileReceiver_BriefDetailsTitle"
                  iconSize={16}
                  expanded
                >
                  {lastUpdatedDate && (
                    <Flex my="4">
                      <Text
                        size="2"
                        weight="bold"
                      >{`Last Updated: ${lastUpdatedDate}`}</Text>
                    </Flex>
                  )}
                  <div className="FileReceiver_BriefDescription">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: sanitize(
                          this.props.inbox.brief_data?.description_html,
                          true,
                        ),
                      }}
                    />
                  </div>
                </Accordion>
              </Box>
            ) : null}
            <div className="row">
              <div className="col-xs-12 col-sm-6">
                <Label caption="Name" validationError={nameMessage} />
                <TextField.Root
                  size="3"
                  id="sender"
                  name="sender"
                  type="text"
                  placeholder="Your Name"
                  autoComplete="name"
                  disabled={
                    Boolean(this.props.discoUser?.id) || this.props.preview
                  }
                  value={senderDefaultValue}
                  onChange={this._onChange}
                />
                <Label caption="Email Address" validationError={emailMessage} />
                <TextField.Root
                  size="3"
                  id="email"
                  name="email"
                  type="email"
                  value={emailDefaultValue}
                  onChange={this._onChange}
                  placeholder="Your Email Address"
                  disabled={
                    Boolean(this.props.discoUser?.id) || this.props.preview
                  }
                  autoComplete="email"
                />

                <Label
                  caption="Playlist Name"
                  validationError={playlistNameMessage}
                >
                  {discoPlaylist && (
                    <DiscoPlaylistActions
                      playlistUrl={discoPlaylist.url}
                      setDiscoPlaylist={this.props.setDiscoPlaylist}
                      changeUrlParams={{
                        path: this.props.params.path,
                        groupId: this.props.params.groupId,
                        groupName: this.props.params.groupName,
                        search: this.props.location.search,
                      }}
                    />
                  )}
                </Label>
                <TextField.Root
                  size="3"
                  id="title"
                  name="title"
                  type="text"
                  value={playlistDefaultName}
                  onChange={this._onChange}
                  placeholder="Something easy to recognise"
                  disabled={!!this.props.discoPlaylist || this.props.preview}
                  autoComplete="off"
                />
              </div>
              <div className="col-xs-12 col-sm-6">
                <Label caption="Message" />
                <TextArea
                  name="message"
                  className="FileReciever_TextArea"
                  defaultValue={this.state.message}
                  onChange={this._onChange}
                  disabled={this.props.preview}
                />
              </div>
            </div>
            {!!customMessage && (
              <div className="FileReciever_FormRow">
                <div className="FileReceiver_CustomMessage">
                  <InlineSVG
                    className="FileReceiver_CustomMessage__icon"
                    src={require('!svg-inline!assets/icons/notification-important.svg')}
                  />
                  <div
                    className="FileReceiver_CustomMessage__text"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(customMessage, true),
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          {!discoPlaylist && (
            <Playlist
              ref={r => {
                this.dropzone = r
              }}
              {...this.props}
            />
          )}
          {playlistMessage && this.renderHelpText(playlistMessage)}
          {!isMobile && <VerticalSpacer />}
          <div className="FileReceiver_ButtonContainer">
            <div className="FileReceiver_Coll FileReceiver_Logo">
              {this.props.businessData.public_metadata?.show_disco_logo && (
                <FooterLogo
                  onClick={() =>
                    mixpanel.sendPureEvent('Click DISCO logo', {
                      'UTM Source': 'disco_playlist',
                      'UTM Medium': 'public_inbox',
                    })
                  }
                  link={DISCO_SITE_SIGNUP_FROM_PUBLIC_INBOX_URL}
                />
              )}
            </div>

            <div className="FileReceiver_Coll">
              <Button
                appearance="primary"
                disabled={isSendDisabled || this.props.preview}
                progressIndicator={sending}
                progressCaption={this.props.submitButtonCaption.progress}
                caption={this.props.submitButtonCaption.caption}
                onClick={this._onSubmit}
                fullWidth={isMobile}
                textCenter
              />
            </div>
          </div>
        </form>
      </PublicContent>
    )
  }

  _onValidate = error => {
    if (!error) {
      const trackIds = this.props.playlist.tempItems
        ? this.props.playlist.tempItems.map(track => track.id)
        : []

      if (this.props.discoPlaylist) {
        this.props.sendDiscoPlaylist(
          {
            ...this.props.discoPlaylist,
            message: this.state.message,
            name: Boolean(this.state.title) ? this.state.title : undefined,
          },
          {
            shouldKeepUploadState: !this.props.inbox.brief_data,
          },
        )
      } else {
        const playlist = {
          sender_name: this.state.sender.trim(),
          sender_email: this.state.email.trim(),
          playlist_name: this.state.title.trim(),
          message: this.state.message,
          track_ids: trackIds,
        }
        this.props.sendPlaylist(playlist, {
          shouldKeepUploadState: !this.props.inbox.brief_data,
        })
      }
    }
  }

  _isSendDisabled() {
    const { tempItems } = this.props.playlist
    const uploadingFile = !!(tempItems ?? []).find(
      item => item.id === undefined,
    )
    return (
      (tempItems?.length === 0 || uploadingFile) && !this.props.discoPlaylist
    )
  }

  _onSubmit = evt => {
    evt.preventDefault()
    if (this._isSendDisabled()) return
    this.props.validate(this._onValidate)
  }
}

const ValidatedFileReceiver = validation(strategy)(FileReceiver)

function FileReceiverWithRouter(props: any) {
  const params = useParams<{
    path: string
    groupId: string
    groupName: string
  }>()
  return <ValidatedFileReceiver {...props} params={params} />
}

export default FileReceiverWithRouter as React.ComponentType<OwnProps>
